import { useCallback, useEffect, useState } from "react";
import TaskHeader from "components/task/header/TaskHeader";
import TaskContent from "components/task/content/TaskContent";
import { Box, Stack } from "@mui/joy";
import { ProgramContentType } from "data/BlockComponent";
import * as _ from "lodash";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import useGetCertainEventFromApp from "hooks/useGetCertainEventFromApp";
import { useSearchParams } from "react-router-dom";
import { MeditationGroupType } from "api/meditationRecordApi";
import { theme } from "styles/theme";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  MEDITATION_RECORD_DOMAIN,
  MEDITATION_RECORD_STATUS_DETAIL_ENDPOINT,
  meditationRecordApi,
} from "api/meditationRecordApi";
import useGetMeditationRecordData from "hooks/useGetMeditationRecordData";
import dayjs from "dayjs";
import { taskListAtom } from "../../../recoil/taskListAtom";

export default function MeditationRecordMobileDetailPage() {
  const [searchParams] = useSearchParams();
  const meditationRecordKey = searchParams.get("mrk");
  const groupTypeParam = searchParams.get("groupType") as
    | MeditationGroupType
    | null;

  const user = useRecoilValue(userAtom);
  const taskList = useRecoilValue(taskListAtom);

  const [data, setData] = useState<ProgramContentType[] | undefined>();
  const [date, setDate] = useState<string | undefined>();

  const queryClient = useQueryClient();

  const [isEmpty, setIsEmpty] = useState<boolean>(false);

  const getStatusQueryFn = useCallback(
    () => meditationRecordApi.getStatus(meditationRecordKey!!),
    [meditationRecordKey],
  );

  // status 는 쿼리 캐시에서 직접 파생한다. DoneButton 이 done/undone 을 setQueryData 로
  // 선반영할 때 onSuccess 는 호출되지 않으므로, 자동 저장 게이트(isDone)가 즉시 따라가게 한다.
  const { data: statusResponse } = useQuery(
    [
      MEDITATION_RECORD_DOMAIN,
      MEDITATION_RECORD_STATUS_DETAIL_ENDPOINT,
      meditationRecordKey,
    ],
    getStatusQueryFn,
    { enabled: !!user?.userId && !!meditationRecordKey },
  );
  const meditationRecordStatus = statusResponse?.result;

  const {
    data: fetchedData,
    refetch,
    isFetching,
  } = useGetMeditationRecordData({
    mrk: meditationRecordKey!!,
    isFirst: isEmpty,
    // 생각기록지 상세와 같이 status(isDone) 확정 후 본문을 로드해, 완료 기록에서
    // 자동 저장이 isDone 을 모르는 채로 나가지 않게 한다.
    enabled:
      !!meditationRecordKey &&
      taskList.length > 0 &&
      meditationRecordStatus !== undefined,
    groupType: groupTypeParam || undefined,
  });

  const refetchActivityEvent = useGetCertainEventFromApp({
    eventName: "refetch_activity",
  });

  useEffect(() => {
    if (refetchActivityEvent) {
      refetch();
    }
  }, [refetchActivityEvent]);

  useEffect(() => {
    if (fetchedData) {
      if (!_.isEqual(fetchedData.content, data)) {
        setData(fetchedData.content);
      }
    }
  }, [fetchedData]);

  useEffect(() => {
    if (data) {
      for (const item of data) {
        if (item.lines) {
          for (const line of item.lines) {
            for (const cell of line) {
              if ((cell as any).type === "date") {
                setDate((cell as any).content.value);
                return;
              }
            }
          }
        }
      }
    }
  }, [data]);

  const [moveToIndex, setMoveToIndex] = useState<number | undefined>(undefined);

  return (
    <Stack
      direction='column'
      sx={{
        height: "100%",
        width: "100%",
        backgroundColor: theme.vars.palette.Background.Level1,
      }}
    >
      <Box
        sx={{
          flex: "none",
          zIndex: 100,
          // px: { xs: "16px", sm: "24px" },
        }}
      >
        <TaskHeader
          key={meditationRecordKey}
          taskKey={`5-7-T:${user?.userId}`}
          meditationRecordKey={meditationRecordKey!!}
          title={date ? `${dayjs(date).format("M월 D일")}` : ""}
          contentData={data}
          setContentData={setData}
          setMoveToIndex={setMoveToIndex}
          isDone={meditationRecordStatus?.isDone}
          lastDoneAt={meditationRecordStatus?.lastDoneAt}
        />
      </Box>
      <Stack sx={{ flex: 1, overflow: "hidden" }} direction={"row"}>
        {meditationRecordKey && (
          <Stack sx={{ flex: 1, overflow: "hidden" }}>
            <TaskContent
              key={meditationRecordKey}
              taskKey={`5-7-T:${user?.userId}`}
              meditationRecordKey={meditationRecordKey}
              data={data}
              setData={setData}
              moveToIndex={moveToIndex}
              setMoveToIndex={setMoveToIndex}
              isDone={meditationRecordStatus?.isDone}
              isOpen={meditationRecordStatus?.isOpen}
              translationVersion={fetchedData?.translationVersion}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
